import { useNavigate } from 'react-router-dom';
import { App, CustomDomainStatus } from '@back4app2/sdk';
import { DsButton } from './ds';
import { RedeployButton } from './MyAppActions';
import { upgradePath } from '../containers/MyAppSettings';

/**
 * What a free plan's address is, and how long it has.
 *
 * A free-plan subdomain is swept on a TTL — `handleExpiredCustomDomains`
 * takes READY rows on a '%Free%' plan once they are older than
 * `FREE_PLAN_DOMAIN_TTL_IN_MINUTES` — and nothing anywhere sets EXPIRING, so
 * the status goes READY → EXPIRED in one step. Without this card the first
 * notice a customer gets that their URL was ever temporary is the URL being
 * gone.
 *
 * Two states, and the difference between them is the tense. Live: it says so
 * and counts down, and the one way out is above the fold rather than behind
 * the Plan card. Gone: it says what happened and what each of the two buttons
 * buys — upgrading keeps an address, redeploying rents another.
 *
 * The term is the plan's TTL, written out, exactly as this card wrote it
 * before the restyle. It is the allowance rather than the time left — an
 * address ten minutes from the sweep still reads "60 minutes" — which is a
 * known trade: saying the real remainder needs the deadline per domain, and
 * the API does not carry one.
 *
 * `Upgrade` lives here rather than in `MyAppActions`, inside the same box as
 * the sentence: the offer and its reason are one thing to read, and a button
 * floating under a card reads as belonging to whatever comes next.
 *
 * It is the screen's one filled green — the DS keeps that rare on purpose.
 * Deploy steps down to the blue secondary while this is on screen, which is
 * what `hasTemporaryUrlNotice` is exported for.
 */
/**
 * Whether the notice draws anything for this app, and which of its two states.
 *
 * Exported so `MyAppActions` can hand the screen's one filled green to the
 * Upgrade button while this is showing, without running the same four tests
 * against a second copy of the rules.
 */
export const hasTemporaryUrlNotice = (app: App): 'live' | 'expired' | null => {
  const env = app.mainService && app.mainService.mainServiceEnvironment;
  const domain = env && env.mainCustomDomain;

  // Only free URLs expire, and only a free plan's redeploy mints a new one —
  // the same test the sweeper and triggerManualDeployment run. On a paid plan
  // there is nothing temporary to report and nothing to sell.
  const isFreePlan = !!env?.plan?.name?.includes('Free');

  // A blocked app must not advertise an address at all, live or expired; the
  // block card on the Overview is the whole of what it has to say.
  if (!domain || !isFreePlan || app.block) {
    return null;
  }

  if (domain.status === CustomDomainStatus.EXPIRED) {
    return 'expired';
  }

  if (domain.status === CustomDomainStatus.READY) {
    return 'live';
  }

  return null;
};

const TemporaryUrlNotice = ({ app }: { app: App }) => {
  const navigate = useNavigate();

  const state = hasTemporaryUrlNotice(app);

  if (!state) {
    return null;
  }

  const isExpired = state === 'expired';

  // One box: heading, sentence, and the button the sentence is the reason for.
  // Redeploy stands outside it, because it answers the card rather than being
  // part of what the card says — and the two buttons have to be adjacent to
  // read as the choice they are: buy an address that stays, or rent another.
  return (
    <div className="flex flex-col gap-2">
      <div className="p-3 rounded-card border border-navy-500 bg-navy-700 flex flex-col gap-2.5">
        <div className="flex flex-col gap-1">
          <span className="font-sora text-ds-135 font-semibold tracking-[-0.01em] text-white">
            {isExpired ? 'Temporary URL Expired' : 'Temporary URL Active'}
          </span>
          {/* The wording this card had before the restyle, word for word. The
              only edit is the spelling of "temporary", which has carried an
              extra letter since 98e578d3. */}
          <p className="m-0 text-ds-115 leading-[1.5] text-navy-200">
            {isExpired ? (
              <>
                Your App temporary URL has expired. Please upgrade to a paid plan to
                continue developing, or redeploy your app to finish your product
                experimentation.
              </>
            ) : (
              <>URL is temporary and will be live for 60 minutes</>
            )}
          </p>
        </div>
        <DsButton
          variant="primary"
          size="lg"
          className="w-full"
          title="Upgrade for a Permanent URL"
          onClick={() => navigate(upgradePath(app.id))}
        >
          Upgrade for a Permanent URL
        </DsButton>
      </div>
      {isExpired ? <RedeployButton app={app} /> : null}
    </div>
  );
};

export default TemporaryUrlNotice;
